import React, { useCallback, useState } from 'react';

import { Button } from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { IconChartBar } from '@tabler/icons-react';
import BackButton from 'components/BackButton';
import InvoiceFilterBar, {
  InvoiceFilterKey,
  InvoiceReportTotal,
} from 'components/invoices/InvoiceFilterBar';
import InvoicesTable, { InvoicesTableSearchFilters } from 'components/invoices/InvoicesTable';
import useUpcomingRevenue from 'hooks/reports/revenue/useUpcomingRevenue';
import { useNavigate } from 'react-router-dom';
import { Invoice } from 'types';
import { formatDateForApi } from 'utils/date-functions';

import UpcomingRevenueProjectionDrawer from './components/UpcomingRevenueProjectionDrawer';

// Status is hidden: the report hard-filters UPCOMING, PAYMENT FAILED and PENDING.
const FILTERS: InvoiceFilterKey[] = ['dueDateRange', 'productType', 'billingSources', 'paymentMethods'];

const UpcomingRevenue = (): JSX.Element => {
  const navigate = useNavigate();
  const [isProjectionOpen, { open: openProjection, close: closeProjection }] = useDisclosure(false);

  const defaultDates = (): [Date, Date] => {
    const date = new Date();
    return [
      new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1),
      new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 59),
    ];
  };

  const initialSearchFiltersValue: InvoicesTableSearchFilters = {
    dueDateRange: defaultDates(),
    productType: '',
    billingSources: [],
    status: ['UPCOMING', 'PAYMENT FAILED', 'PENDING'] as Array<Invoice['status']>,
    paymentMethods: [],
  };

  const [searchFilters, setSearchFilters] = useState<InvoicesTableSearchFilters>(initialSearchFiltersValue);
  const [debouncedSearchFilters] = useDebouncedValue<InvoicesTableSearchFilters>(searchFilters, 250);
  const [orderingValue, setOrderingValue] = useState('dueDate');

  const total = useUpcomingRevenue({
    ...searchFilters,
    dueDateStart: formatDateForApi(searchFilters.dueDateRange?.[0]) ?? null,
    dueDateEnd: formatDateForApi(searchFilters.dueDateRange?.[1]) ?? null,
  });

  const handleBackClick = useCallback((): void => {
    navigate(-1);
  }, [navigate]);

  // The total is meaningless without both ends of the range, so it stays hidden
  // until the picker has one.
  const hasFullRange = !!(searchFilters.dueDateRange?.[0] && searchFilters.dueDateRange?.[1]);

  return (
    <>
      <InvoiceFilterBar
        source='upcoming-revenue'
        title='Upcoming Revenue'
        filters={FILTERS}
        searchFilters={searchFilters}
        setSearchFilters={setSearchFilters}
        initialSearchFiltersValue={initialSearchFiltersValue}
        orderingValue={orderingValue}
        csvReportType='upcoming-revenue'
        csvStartDate={formatDateForApi(searchFilters.dueDateRange?.[0]) ?? null}
        csvEndDate={formatDateForApi(searchFilters.dueDateRange?.[1]) ?? null}
        leftSection={<BackButton handleOnClick={handleBackClick} />}
        titleDataCy='totalRevenuePageHeader'
        titleSuffix={<InvoiceReportTotal query={total} color='kiloBlue' isReady={hasFullRange} />}
        extraActions={
          <Button leftIcon={<IconChartBar />} onClick={openProjection}>
            Open Revenue Projection
          </Button>
        }
        dueDateMinDate={new Date()}
        dueDateClearable={false}
      />

      <InvoicesTable searchFilters={debouncedSearchFilters} setOrderingValue={setOrderingValue} />

      <UpcomingRevenueProjectionDrawer opened={isProjectionOpen} onClose={closeProjection} />
    </>
  );
};

export default UpcomingRevenue;
